import React, { useState } from 'react';

import { useSelector } from 'react-redux';

import { useMediaQuery, useTheme } from '@material-ui/core';
import Badge from '@material-ui/core/Badge';

import { employeesForOrgIdSelector } from '../../reducers/employee-selectors';

import { UiFilterTextField } from '../../ui/ui-filter-text-field';
import { isEmployeeMessage, mapMessageToGenericMessageItem } from '../employee-message-utils';

import { RecipientListItem } from './recipient-list-item';

import { getEmployeeGroupCountsFromEmployees } from '../../employees/utils/get-employee-groups-from-employees';
import { allEmployeesKey } from '../../util/all-employees-option';
import { ICON_NAMES, MaterialDesignIcon } from '../../util/material-design-icons';
import { sortByDate } from '../../util/sort-functions';

type InputProps = {
  orgId: number;
  messages: Array<EmployeeOrgMessage>;
  announcements: Array<AnnouncementSummary>;
  messageThreadPreviewRef: React.RefObject<HTMLDivElement>;
  activeEmployeeId: number | null;
  activeGroupId: string | null;
  isComposingNewMessage: boolean;
  handleMessageClick: ({
    employeeId,
    groupId,
  }: {
    employeeId?: number | null;
    groupId?: string | number | null;
  }) => () => void;
};

export const RecipientList = (props: InputProps) => {
  const {
    orgId,
    messages,
    announcements,
    messageThreadPreviewRef,
    activeEmployeeId,
    activeGroupId,
    isComposingNewMessage,
    handleMessageClick,
  } = props;

  const [searchTerm, setSearchTerm] = useState<string>('');

  const theme = useTheme();
  const isOnMobile = useMediaQuery(theme.breakpoints.down('xs'));
  const employees = useSelector((state: any) => employeesForOrgIdSelector(state, orgId));
  const currentEmployees = (employees || []).filter((emp) => emp.isCurrentEmployee);

  const messagesAndAnnouncements = (
    messages as Array<EmployeeOrgMessage | AnnouncementSummary>
  ).concat(announcements);
  const unreadMessages = messages.filter((m) => m.unread);
  const readMessagesAndAnnouncements = messagesAndAnnouncements
    .filter((m) => !isEmployeeMessage(m) || !m.unread)
    .sort(sortByDate((message) => message.sentAt));

  const groupMembershipCounts = Object.assign(
    getEmployeeGroupCountsFromEmployees(currentEmployees),
    { [allEmployeesKey(orgId)]: currentEmployees.length },
  );

  const handleSearchTermChange = (value: string) => {
    setSearchTerm(value);
  };

  const filterMessagesBySearchTerm = (
    messagesToFilter: Array<EmployeeOrgMessage | AnnouncementSummary>,
  ) => {
    if (!searchTerm) {
      return messagesToFilter;
    }

    return messagesToFilter.filter((m) => isSearchTermMatch(m));
  };

  const isSearchTermMatch = (message: EmployeeOrgMessage | AnnouncementSummary) => {
    const strippedSearchTerm = searchTerm.replace(' ', '').toLowerCase();
    const genericMessageItem = mapMessageToGenericMessageItem(message);
    const strippedRecipientName = genericMessageItem.recipientName.replace(' ', '').toLowerCase();
    return strippedRecipientName.includes(strippedSearchTerm);
  };

  const hiddenForMobileClass =
    isOnMobile && (activeEmployeeId || isComposingNewMessage || activeGroupId)
      ? 'u-display-none'
      : '';
  const mobileClass = isOnMobile ? 'u-width-100 u-flexShrink-0' : '';
  const threadPaneClasses = `u-width-300px u-border u-border-topRight u-overflowY-auto u-flexShrink-0 ${mobileClass} ${hiddenForMobileClass}`;
  const categoryBannerClasses = `u-padding-y-sm u-padding-right-lg u-padding-left-md u-textTransform-uppercase typo-medium u-background-lightGray u-display-flex u-justifyContent-spaceBetween u-alignItems-center`;

  const filteredUnreadMessages = filterMessagesBySearchTerm(
    unreadMessages,
  ) as Array<EmployeeOrgMessage>;
  const filteredReadMessagesAndAnnouncements = filterMessagesBySearchTerm(
    readMessagesAndAnnouncements,
  );

  return (
    <div className={threadPaneClasses} ref={messageThreadPreviewRef}>
      <UiFilterTextField
        className="u-margin-x u-margin-y"
        value={searchTerm}
        onChange={handleSearchTermChange}
        placeholder="Search Recipients"
        startAdornment={
          <MaterialDesignIcon
            iconName={ICON_NAMES.SEARCH}
            size="sm"
            className="u-margin-right-xs"
          />
        }
        fullWidth
      />
      {filteredUnreadMessages.length ? (
        <div className={categoryBannerClasses}>
          Unread
          <Badge
            badgeContent={filteredUnreadMessages.length}
            max={10}
            color="secondary"
            className="u-margin-right"
          >
            <span />
          </Badge>
        </div>
      ) : null}
      {filteredUnreadMessages.map((message) => (
        <RecipientListItem
          key={`message-${message.messageId}`}
          orgId={orgId}
          message={message}
          activeEmployeeId={activeEmployeeId}
          activeGroupId={activeGroupId}
          isComposingNewMessage={isComposingNewMessage}
          handleMessageClick={handleMessageClick}
          groupMembershipCounts={groupMembershipCounts}
        />
      ))}
      {filteredReadMessagesAndAnnouncements.length ? (
        <div className={categoryBannerClasses}>All Messages</div>
      ) : null}
      {filteredReadMessagesAndAnnouncements.map((message) => (
        <RecipientListItem
          key={`${
            isEmployeeMessage(message)
              ? `message-${message.messageId}`
              : `announcement-${message.announcementId}`
          }`}
          orgId={orgId}
          message={message}
          activeEmployeeId={activeEmployeeId}
          activeGroupId={activeGroupId}
          isComposingNewMessage={isComposingNewMessage}
          handleMessageClick={handleMessageClick}
          groupMembershipCounts={groupMembershipCounts}
        />
      ))}
      {searchTerm &&
      !(filteredUnreadMessages.length || filteredReadMessagesAndAnnouncements.length) ? (
        <div className="typo-body u-border-top u-textAlign-center u-padding-top">
          No recipients match your search
        </div>
      ) : null}
    </div>
  );
};
